import { reaction } from 'mobx';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { ILockService } from '@src/slide/application/service/lock-service/lock-service.interface';
import ColorPicker from '@src/framework/frame-pc/components/color-picker';
import { ISelectedAnimManager } from './selected-anim-manager.interface';
import { ISelectionService } from './selection-service.interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';
import { ICursorManager } from '@src/slide/application/service/cursor-manager/core/cursor-manager.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IUserPriviledgeService } from '../user-priviledge-service/user-priviledge-service.interface';

export class SelectionEventService {
    private selectionService?: ISelectionService;
    private masterLayoutEditService?: IMasterLayoutEditService;
    private selectedAnims?: ISelectedAnimManager;
    private lockService?: ILockService;
    private historyManager?: IHistoryManager;
    private cursorManager?: IDynamicService<ICursorManager>;
    private registeredSelectedEventCallbacks: Set<() => void> = new Set();
    private privilegeService: IUserPriviledgeService;

    public setSelectionServiceDelegate({ selectionService, privilegeService }: {
        selectionService: ISelectionService;
        privilegeService: IUserPriviledgeService;
    }): void {
        if (this.selectionService) return;
        this.selectionService = selectionService;
        this.privilegeService = privilegeService;
        // currentSlideIndex 变化 清空 element 选择状态
        reaction(
            () => [
                this.selectionService?.currentSlideIndex,
                this.selectionService?.currentSlideType,
                this.selectionService?.selectedSlides.selectedArray.map(e => e.id),
            ],
            this.selectedSlideEventCallback,
        );
        reaction(
            () => [
                this.selectionService?.selectedElements.selectedArrayDic[SelectedEleMgrType.SLIDE].length,
                this.selectionService?.selectedElements.selectedArrayDic[SelectedEleMgrType.GROUP].length,
            ],
            this.selectedElementEventCallback,
        );
    }

    public setMasterLayoutEditServiceDelegate(masterLayoutEditService: IMasterLayoutEditService): void {
        if (this.masterLayoutEditService) return;
        this.masterLayoutEditService = masterLayoutEditService;
        // editType、currentSlideType 监听，母版编辑改动
        reaction(() => this.masterLayoutEditService?.editType, this.selectedSlideEventCallback);
    }

    public setSelectedAnimManagerDelegate(selectedAnimManager: ISelectedAnimManager): void {
        if (this.selectedAnims) return;
        this.selectedAnims = selectedAnimManager;
    }

    public setLockServiceDelegate(lockService: ILockService): void {
        if (this.lockService) return;
        this.lockService = lockService;
    }

    public setHistoryManagerDelegate(historyManager: IHistoryManager): void {
        if (!this.historyManager) {
            this.historyManager = historyManager;
        }
    }

    public setCursorManagerDelegate(cursorManager: IDynamicService<ICursorManager>): void {
        if (!this.cursorManager) {
            this.cursorManager = cursorManager;
        }
    }

    /**
     * 注册选中元素变更时的事件回调
     * @param callback
     */
    public registerSelectedElementEventCallback(callback: () => void): void {
        this.registeredSelectedEventCallbacks.add(callback);
    }

    public unregisterSelectedElementEventCallback(callback: () => void): void {
        this.registeredSelectedEventCallbacks.delete(callback);
    }

    private selectedSlideEventCallback = () => {
        this.selectedAnims?.clear();
        const editable = SlideDataServices.PrivilegeManager()?.editable;
        // 检查协同锁
        if (!this.historyManager?.isShowingHistory && editable) {
            this.lockService?.checkLock();
        }
        // 清空所有协作光标标签
        this.cursorManager?.promise().then(cursorManager => cursorManager.clearCursorTagsForElements());
        OnlineEditServices.CursorManager()?.reloadCursor();

        if (ColorPicker.isShown()) {
            ColorPicker.hide();
        }
    };

    private selectedElementEventCallback = () => {
        this.registeredSelectedEventCallbacks.forEach(callback => callback());
    };
}
